import { Checkbox } from '@zoopla-eng/zds-components/checkbox';
import {
    ChevronDownMedium,
    ChevronUpMedium
} from '@zoopla-eng/zds-components/icons';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { fontGroups } from '@zoopla-eng/zds-components/themes';
import { getPropertyType } from '@zoopla/listing-search/get-property-type';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { DropdownContainer } from '../DropdownContainer';
import { onDocumentClick } from '../utils/onDocumentClick';
import {
    itemStyle,
    selectPropertyTypeArrow,
    selectPropertyTypeButton,
    selectPropertyTypeLabel,
    selectPropertyTypeText,
    selectPropertyTypeWrapperStyle
} from './SelectPropertyType.styles.css';

const propertyTypes = ['property', 'houses', 'flats', 'farms_land'] as const;

const propertySubTypeOptions = {
    detached: 'Detached',
    semi_detached: 'Semi-detached',
    terraced: 'Terraced',
    flats: 'Flats',
    bungalow: 'Bungalows',
    farms_land: 'Farms/land',
    park_home: 'Park homes'
};

export type PropertyType = (typeof propertyTypes)[number];
export type PropertySubType = Array<keyof typeof propertySubTypeOptions>;

type SelectPropertyTypeProps = {
    value: { propertyType: PropertyType; propertySubType: PropertySubType };
    onChange: ({
        propertySubType,
        propertyType
    }: {
        propertySubType: PropertySubType;
        propertyType: PropertyType;
    }) => void;
    disabled: boolean;
};

export const SelectPropertyType = ({
    value,
    onChange,
    disabled = false
}: SelectPropertyTypeProps) => {
    const [isActive, setIsActive] = useState(false);
    const rootNode = useRef() as MutableRefObject<HTMLDivElement>;

    const toggle = () => {
        setIsActive(!isActive);
    };

    const buttonText =
        value.propertySubType
            .map((propertySubType) => propertySubTypeOptions[propertySubType])
            .join(', ') || 'Show all';

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(
        onDocumentClick((evt: globalThis.MouseEvent) => {
            if (
                rootNode.current &&
                rootNode.current.contains(evt.target as Node)
            ) {
                return;
            }

            setIsActive(false);
        }),
        []
    );

    return (
        <div
            data-testid="PropertyType_testId"
            className={selectPropertyTypeWrapperStyle}
            ref={rootNode}
        >
            <button
                className={`${selectPropertyTypeButton} ${fontGroups.ladle}`}
                onClick={toggle}
                disabled={disabled}
            >
                <label className={selectPropertyTypeLabel}>Property type</label>
                <span className={selectPropertyTypeText}>{buttonText}</span>
                <div className={selectPropertyTypeArrow} aria-hidden>
                    {isActive ? <ChevronUpMedium /> : <ChevronDownMedium />}
                </div>
            </button>
            <DropdownContainer isActive={isActive} isAlignedRight>
                <Stack
                    flexDirection="row"
                    flexWrap="wrap"
                    columnGap={0}
                    rowGap="S400"
                >
                    {[
                        <div key="quicksearch_show_all" className={itemStyle}>
                            <Checkbox
                                disabled={disabled}
                                name="show_all"
                                label="Show all"
                                checked={value.propertySubType.length === 0}
                                onCheckedChange={() => {
                                    onChange({
                                        propertySubType: [],
                                        propertyType: 'property'
                                    });
                                }}
                            />
                        </div>,
                        ...Object.keys(propertySubTypeOptions).map(
                            (propertySubType) => {
                                return (
                                    <div
                                        key={`quicksearch_${propertySubType}`}
                                        className={itemStyle}
                                    >
                                        <Checkbox
                                            disabled={disabled}
                                            name={propertySubType}
                                            label={
                                                propertySubTypeOptions[
                                                    propertySubType as PropertySubType[number]
                                                ]
                                            }
                                            checked={value.propertySubType.includes(
                                                propertySubType as PropertySubType[number]
                                            )}
                                            onCheckedChange={(checked) => {
                                                if (checked) {
                                                    const _propertySubType = [
                                                        ...value.propertySubType,
                                                        propertySubType
                                                    ];

                                                    onChange({
                                                        propertySubType:
                                                            _propertySubType as PropertySubType,
                                                        propertyType:
                                                            getPropertyType(
                                                                _propertySubType as PropertySubType
                                                            )
                                                    });
                                                } else {
                                                    const _propertySubType =
                                                        value.propertySubType.filter(
                                                            (i) =>
                                                                i !==
                                                                propertySubType
                                                        );

                                                    onChange({
                                                        propertySubType:
                                                            _propertySubType,
                                                        propertyType:
                                                            getPropertyType(
                                                                _propertySubType
                                                            )
                                                    });
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }
                        )
                    ]}
                </Stack>
            </DropdownContainer>
        </div>
    );
};
